<template>
    <div v-if="visible">
        <v-dialog
            v-model="dialog"
            persistent
            max-width="500"
        >

            <v-card>
                <v-form ref="form" lazy-validation @submit="onSubmit">
                    <v-card-title class="px-3">Take Payment</v-card-title>
                    <v-divider></v-divider>

                    <v-container fluid>
                        <v-row>
                            <v-col md="12">
                                <v-select
                                    v-model="card"
                                    :items="activeCards"
                                    :rules="[rules.required]"
                                    required
                                    dense
                                    label="Select Card"
                                    return-object
                                    @change="response = null"
                                >
                                </v-select>
                            </v-col>

                            <!-- Existing Card -->
                            <v-col v-if="card && card.value" md="12" class="pb-0" align="center" justify="center">
                                <v-img
                                    v-show="paymentBrand == 'visa'"
                                    src="@/assets/visa.png" width="200"
                                    transition="fade-transition"></v-img>
                                <v-img 
                                    v-show="paymentBrand == 'master'"
                                    src="@/assets/mastercard-small.png"
                                    width="200"
                                    transition="fade-transition"></v-img>

                                <div>
                                    <div class="mt-8"><b>Card Number: </b>{{ `${card.cardData.bin}******${card.cardData.last4Digits}` }}</div>
                                    <div class="mt-2"><b>Card Holder: </b>{{ card.cardData.holder }}</div>
                                    <div class="mt-2"><b>Card Expiry: </b>{{ `${card.cardData.expMonth}/${card.cardData.expYear}` }}</div>
                                    <div class="mt-2"><b>Add On: </b>{{ card.cardData.createdAt }}</div>
                                    <div class="mt-2"><b>Amount: </b>{{ `${paymentData.currency} ${paymentData.amount}` }}</div>
                                </div>
                            </v-col>
                           
                            <!-- New Card -->
                            <v-row v-if="card && !card.value" class="px-3 pt-0">
                                <!-- Card Holder -->
                                <v-col md="6" class="pb-0 pt-0">
                                    <v-text-field
                                        v-model="newCard.holder"
                                        :rules="[rules.required]"
                                        required
                                        dense
                                        label="Card Holder"
                                    ></v-text-field>
                                </v-col>

                                <!-- Amount -->
                                <v-col md="6" class="pb-0 pt-0">
                                    <v-text-field
                                        v-model="paymentData.amount"
                                        :rules="[rules.required]"
                                        required
                                        dense
                                        label="Amount"
                                        disabled
                                    ></v-text-field>
                                </v-col>
                                
                                <!-- Address 1 -->
                                <v-col md="6" class="pb-0">
                                    <v-text-field
                                        v-model="newCard.address1"
                                        :rules="[rules.required]"
                                        required
                                        dense
                                        label="1st Line Address"
                                    ></v-text-field>
                                </v-col>

                                <!-- Postcode -->
                                <v-col md="6" class="pb-0">
                                    <v-text-field
                                        v-model="newCard.postcode"
                                        :rules="[rules.required]"
                                        required
                                        dense
                                        label="Postcode"
                                    ></v-text-field>
                                </v-col>

                                <!-- Card Number -->
                                <v-col md="12" class="pb-0">
                                    <v-text-field
                                        v-model="newCard.cardNumber"
                                        :rules="[rules.required, rules.minNumDigits(15)]"
                                        required
                                        dense
                                        label="Card Number"
                                        v-mask="masks.number"
                                    ></v-text-field>
                                </v-col>

                                <!-- Expiry Month -->
                                <v-col md="4">
                                    <v-text-field
                                        v-model="newCard.expMonth"
                                        :rules="[rules.required, rules.eqNumDigits(2)]"
                                        required
                                        dense
                                        label="Expiry Month"
                                        v-mask="masks.expiryMonth"
                                    ></v-text-field>
                                </v-col>

                                <!-- Expiry Year -->
                                <v-col md="4">
                                    <v-text-field
                                        v-model="newCard.expYear"
                                        :rules="[rules.required, rules.eqNumDigits(4)]"
                                        required
                                        dense
                                        label="Expiry Year"
                                        v-mask="masks.expiryYear"
                                    ></v-text-field>
                                </v-col>

                                <!-- CVV -->
                                <v-col md="4">
                                    <v-text-field
                                        v-model="newCard.cvv"
                                        :rules="[rules.required, rules.minNumDigits(3)]"
                                        required
                                        dense
                                        label="CVV"
                                        v-mask="masks.cvv"
                                    ></v-text-field>
                                </v-col>
                            </v-row>

                            <v-col v-if="response && response.result.code == '000.000.000'" md="12" class="py-0">
                                <v-alert
                                    type="success"
                                    text border="left"
                                    dismissible
                                    class="mb-2">
                                    Code: {{ response.result.code }}<br>
                                    Description: {{ response.result.description }}
                                </v-alert>
                            </v-col>

                            <v-col v-else-if="response && response.result.code != '000.000.000'" md="12" class="py-0">
                                <v-alert type="error" text border="left" dismissible>
                                    Code: {{ response.result.code }}<br>
                                    Description: {{ response.result.description }}
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-container>

                    <v-divider></v-divider>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="error" @click="$emit('close', false); response = null; submitted = false">Close</v-btn>
                        <v-btn :disabled="submitted" color="primary" type="submit">Take Payment</v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import { mask } from 'vue-the-mask';

export default {
    props: {
        visible: Boolean,
        client: Object,
        cardData: Array,
        paymentData: Object
    },
    computed: {
        dialog() {
            return this.visible ? true : false;
        },
        paymentBrand() {
            return (this.card && this.card.value && this.card.cardData.bin.toString().startsWith(4)) ? 'visa' :
                   (this.card && this.card.value && this.card.cardData.bin.toString().startsWith(5)) ? 'master' : false
        },
        activeCards() {
            let cards = [{ text:'Take a payment using a new card', value: null, cardData: null }];
            this.cardData.filter(e => {
                if(e.state === 'active') {
                    cards.push({ text: `${e.card.bin}******${e.card.last4Digits} - ${e.card.paymentBrand} - ${e.card.holder}`,
                                 value: e.card.id,
                                 cardData: e.card });
                }
            });
            return cards;
        }
    },
    directives: { mask },
    data() {
        return {
            rules: {
                required:  v => !!v || 'Required',
                eqNumDigits(numDigits) {
                    return v => (v.replace(/\s/g, '') || '').length == numDigits || 'Field must be ' + numDigits + ' digits long';
                },
                minNumDigits(numDigits) {
                    return v => (v.replace(/\s/g, '') || '').length >= numDigits || 'Field must be greater than ' + numDigits + ' digits long';
                },
            },
            response: null,
            card: { value: null, text: 'Take a payment using a new card' },
            masks: {
                number: '#### #### #### ####',
                expiryMonth: '##',
                expiryYear: '####',
                cvv: '####'
            },
            newCard: {
                holder: null,
                address1: null,
                postcode: null,
                cardNumber: null,
                expMonth: null,
                expYear: null,
                cvv: null
            },
            submitted: false,
        }
    },
    methods: {
        onSubmit(e) {
            e.preventDefault();
            console.log(this.paymentData);
            console.log(this.client);

            if(this.$refs.form.validate()) {

                this.submitted = true;

                // New payment
                if(this.card.value == null) {
                    axios({
                    method: 'post',
                    url: 'payment-live',
                    data: { userId: this.$store.state.user.id,
                            client: this.client,
                            payment: this.paymentData,
                            newCard: this.newCard },
                    config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                    })
                    .then(response => {
                        if(response.data.result.code == '000.000.000') {
                            this.$refs.form.reset();
                            this.submitted = true;
                        } else {
                            this.submitted = false;
                        }
                        this.response = response.data;
                        this.$emit('updatePayments');
                        this.$emit('getSchedules');
                    })
                    .catch(e => {
                        this.submitted = false;
                        this.errors.push(e)
                    })
                }

                // Existing card
                else {

                    let payload = {
                        card: this.card.cardData,
                        userId: this.$store.state.user.id,
                        client: this.client,
                        payment: this.paymentData,
                        paymentType: 'MOTO',
                        currency: this.paymentData.currency,
                    };

                    axios({
                    method: 'post',
                    url: 'recurring-payment-live',
                    data: payload,
                    config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                    })
                    .then(response => {
                        if(response.data.result.code == '000.000.000') {
                            this.$refs.form.reset();
                            this.submitted = true;
                        } else {
                            this.submitted = false;
                        }
                        this.response = response.data;
                        this.$emit('updatePayments');
                        this.$emit('getSchedules');
                    })
                    .catch(e => {
                        this.submitted = false;
                        this.errors.push(e)
                    })
                }
            }
        }
    }
}
</script>

<style>
    .take-payment-label input {
        padding: 0px !important;
    }
</style>